import { useNavigate } from 'react-router-dom';
import { DialogId, useDialog } from 'account/recurring-payment/lib';
import useAppContext from 'core/hooks/app-context';
import PageRoute from 'core/page-route';
import { PaymentApi } from 'core/services/api/payment/payment-api';
import { AnalyticsEventStore } from 'core/stores/analytics-events.store';
import RecurringPaymentStoreManager from 'core/stores/payment/recurring-payment.store.manager';
import useMakePaymentLogic from './use-make-payment.logic';
import useTriggerRecurringPaymentSurvey, { SurveyType } from './use-trigger-recurring-payment-survey';
import tracking from '../tracking';
import { useUpdateTranslations } from '../translations';
import useCancelTranslations from '../translations/cancel.translations';
import useComponentContext from '../use-component-context';

const useManageLogic = () => {
  const navigate = useNavigate();

  const context = useComponentContext();
  const triggerSurvey = useTriggerRecurringPaymentSurvey();

  const appStore = useAppContext();
  const { countryName, customerNumber, recurringPaymentStore } = appStore;

  const paymentLogic = useMakePaymentLogic(true);
  const update = useUpdateTranslations();
  const cancel = useCancelTranslations();

  const dialog = {
    confirmCancellation: useDialog(DialogId.cancelRecurringPayment),
    cancellationConfirmation: useDialog(DialogId.cancelRecurringPaymentConfirmation),
    updating: useDialog(DialogId.updating),
    changeCard: useDialog(DialogId.changeCard),
    setupConfirmation: useDialog(DialogId.setupConfirmation),
    changePaymentDate: useDialog(DialogId.changePaymentDate),
    changeCardFailure: useDialog(DialogId.changeCardFailure),
  };

  const redirecting = context.redirecting;

  const handlePaymentError = (error: any) => {
    redirecting.close();
    context.showError(update.failure.text, update.failure.heading, 'tryAgain');
  }

  const updatePayment = async () => {
    redirecting.open();

    await paymentLogic.makePayment(handlePaymentError);

    redirecting.close();
  }

  const receiveCardDetails = async (event: MessageEvent) => {
    try {
      await paymentLogic.receiveCardDetails(event);
    } catch (error) {
      redirecting.close();
      dialog.changeCardFailure.open();
      console.error(event, error);
    }
  }

  const confirmCardReplacement = async () => {
    dialog.changeCard.close();

    await updatePayment();
  }

  const confirmCancellation = async () => {
    dialog.confirmCancellation.close();
    dialog.updating.open();

    const api = new PaymentApi(customerNumber, countryName);
    const result = await api.cancelRecurringPayment();

    dialog.updating.close();

    await RecurringPaymentStoreManager.initializeFromAppContext(appStore, true);

    const isSuccess = result || !recurringPaymentStore.hasRecurringPayment;

    isSuccess
      ? dialog.cancellationConfirmation.open()
      : context.showError(cancel.failure.text, cancel.failure.heading);

    const cancelTracking = tracking.manage.cancel;

    const event = isSuccess
      ? cancelTracking.successEvent
      : cancelTracking.failureEvent;

    AnalyticsEventStore.setDataLayer(event)

    if (isSuccess) {
      triggerSurvey(SurveyType.Cancel);
    }
  }

  return {
    gotoViewBalance: () => navigate(PageRoute.account.viewBalance),

    changeCard: dialog.changeCard.open,
    cancelChangeCard: dialog.changeCard.close,

    confirmCardReplacement,

    receiveCardDetails,

    declineCardReplacement: dialog.changeCard.close,

    cancelPayment: dialog.confirmCancellation.open,
    cancelDone: dialog.cancellationConfirmation.close,

    cancelChangePaymentDate: dialog.changePaymentDate.close,
    changePaymentDate: dialog.changePaymentDate.open,

    chatToAnAgent: () => {
      appStore.changeOpenLiveChat(true);
      dialog.changePaymentDate.close();
    },

    confirmCancellation,

    declineCancellation: dialog.confirmCancellation.close,

    confirmationDone: dialog.setupConfirmation.close,

    gotoDashboard: () => navigate(PageRoute.dashboard),

    tryAgain: () => {
      dialog.changeCardFailure.close();
      dialog.changeCard.open();
    }
  };
}

export default useManageLogic;